import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
} from '@angular/common/http';
import { StorageService } from '../services/storage.service';
import { Observable } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { Auth0Service } from 'src/app/auth/auth0.service';
import { StorageType } from '../models/constants';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Auth0HttpInterceptor implements HttpInterceptor {
  constructor(
    private auth: Auth0Service,
    private storageService: StorageService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    // Only attach auth headers to own-domain API requests
    if (!this.isAllowedUrl(req.url)) {
      return next.handle(req);
    }

    const token = this.storageService.get(StorageType.auth0Token);
    if (token) {
      const tokenReq = this.cloneRequest(req, token);
      return next.handle(tokenReq);
    } else {
      const tokenReq = this.cloneRequest(req);
      return next.handle(tokenReq);
    }
  }

  getTokenSilently(req: HttpRequest<any>, next: HttpHandler) {
    return this.auth.getTokenSilently$().pipe(
      switchMap(token => {
        this.storageService.set(StorageType.auth0Token, token);
        const tokenReq = this.cloneRequest(req, token);
        return next.handle(tokenReq);
      }),
      catchError(() => {
        const tokenReq = this.cloneRequest(req);
        return next.handle(tokenReq);
      }),
    );
  }

  /**
   * Determine if a URL should receive auth headers.
   * Only same-origin (relative) URLs and known API base URLs are allowed.
   */
  private isAllowedUrl(url: string): boolean {
    // Relative URLs (e.g. /api/...) are same-origin — always allowed
    if (url.startsWith('/')) {
      return true;
    }

    // Check against known API base URLs from environment
    const allowedBases = [
      environment.api_url_v1,
      environment.api_url_v2,
      environment.singleton_service_url,
    ].filter(Boolean);

    return allowedBases.some(base => url.startsWith(base));
  }

  cloneRequest(req, token = null) {
    if (token) {
      return req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
          SourceDomain: window.location.hostname,
          'Auth0-Client-Id': environment.auth0.clientId,
          'Hydra-Enabled': environment.hydraEnabled ? 'true' : 'false',
          'Client-Name-Space': environment.supplierConfig.clientNameSpace,
          CatalogType:
            environment.catalogType[window.location.hostname] || 'medigi',
        },
        withCredentials: true,
      });
    } else {
      return req.clone({
        setHeaders: {
          SourceDomain: window.location.hostname,
          'Auth0-Client-Id': environment.auth0.clientId,
          'Hydra-Enabled': environment.hydraEnabled ? 'true' : 'false',
          'Client-Name-Space': environment.supplierConfig.clientNameSpace,
          CatalogType:
            environment.catalogType[window.location.hostname] || 'medigi',
        },
        withCredentials: true,
      });
    }
  }
}
